/* eslint-disable no-console */
/**
 * Check if Sentry is enabled based on environment and configuration
 * @returns {boolean} True if Sentry should be used
 */
const isSentryEnabled = () => {
  const isDevMode = process.env.CLOUD_DEV === 'true' || window.pov_config?.VUE_APP_ENV === 'development';
  return !isDevMode && window.pov_config?.VUE_APP_FRONTAGE_POV_SENTRY_DSN;
};

/**
 * Centralized error logging utility
 * Sends errors to Sentry and Datadog RUM if available, always logs to console
 * 
 * @param {string} message - Descriptive error message explaining what went wrong
 * @param {Error} error - The actual error object
 * @param {Object} context - Additional context (optional)
 */
const logError = (message, error, context = {}) => {
  if (typeof message !== 'string') {
    console.error('logError: First argument must be a descriptive message string');
    return;
  }

  if (!error) {
    console.error('logError: Second argument (error) is required');
    return;
  }

  // Send to Sentry if enabled (using dynamic import to avoid parser issues)
  // This runs in the background and doesn't block execution
  if (isSentryEnabled()) {
    import('@sentry/vue')
      .then(({ captureException }) => {
        captureException(error, {
          tags: { source: 'logError' },
          extra: { message, ...context },
          fingerprint: [message],
        });
      })
      .catch((sentryError) => {
        // Dynamic import failed - continue with other logging methods
        console.warn('Failed to dynamically load Sentry:', sentryError);
      });
  }

  // Send to Datadog RUM if available
  if (window.datadogRum && typeof window.datadogRum.addError === 'function') {
    window.datadogRum.addError(error, {
      message,
      ...context,
    });
  }

  // Always log to console as fallback
  console.group('🚨🚨🚨 logError start:');
  console.error(`Error message: ${message}`);
  console.error(error);
  if (Object.keys(context).length > 0) {
    console.error('Error context:', context);
  }
  console.groupEnd();
};

export default logError; 
